import { directUpload } from "@/queries/uploads";
import type { FileType } from "@/types/common/files";
import axios from "@/utils/axios";
import { useMutation, useQuery } from "@tanstack/react-query";

export interface UploadFilePayload {
  file: File;
  entity_id: string;
  entity_type: string;
  collection?: string;
}

export interface UploadFileResponse {
  id: string;
  name: string;
  url: string;
  file_name: string;
  collection: string;
  size: number;
  model_id: string;
  model_type: string;
  created_by: string | null;
  updated_at: string | null;
  created_at: string | null;
}

export const uploadFile = async ({
  file,
  entity_id,
  entity_type,
  collection,
}: UploadFilePayload): Promise<{ data: UploadFileResponse }> => {
  const { id: upload_id } = await directUpload(file, {
    context: entity_type,
    context_id: entity_id,
  });
  // Si `saveFileReference` falla aquí, el upload ya quedó COMPLETED y no hay
  // nada que hacer desde el cliente: el backend rechaza cancelar un upload
  // ya completado (ver `use-document-uploads.ts:269-271`), y los confirmados
  // que nunca se asociaron los recoge una limpieza nocturna del backend.
  const response = await saveFileReference({
    upload_id,
    entity_id,
    entity_type,
    collection,
  });
  return response;
};

export const getFile = async (fileId: string): Promise<{ data: FileType }> => {
  const response = await axios.get(`/admin/v1/file/${fileId}`, {
    headers: {
      Accept: "application/json",
    },
  });
  return response.data;
};

interface SaveFileReferencePayload {
  upload_id: string;
  entity_id: string;
  entity_type: string;
  collection?: string;
}

export const saveFileReference = async ({
  upload_id,
  entity_id,
  entity_type,
  collection,
}: SaveFileReferencePayload) => {
  const response = await axios.post(
    "admin/v1/file",
    {
      upload_id,
      entity_id,
      entity_type,
      ...(collection ? { collection } : {}),
    },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );

  return response.data;
};

export const useGetFile = (fileId: string, enabled = true) => {
  return useQuery({
    queryKey: ["file", fileId],
    queryFn: () => getFile(fileId),
    enabled: !!fileId && enabled,
    staleTime: 55 * 60 * 1000, // 55 minutes
  });
};

export const useDeleteFile = () => {
  return useMutation({
    mutationFn: async (fileId: string) => {
      const response = await axios.delete(`/admin/v1/file/${fileId}`, {
        headers: {
          Accept: "application/json",
        },
      });
      return response.data;
    },
  });
};
